// Presentation helpers for the Markdown pane (reference mdPaneVals L657–717). Calendar/locale follow the user's
// presentation settings (calendar.ts); storage values stay ISO-8601 UTC.
import { t } from '@/app/i18n';
import { tpCal, tpLoc } from '@/modules/panes/calendar';
import type { MdVersion } from './api';

/** Byte size label: "{n} B" below 1 KB, else rounded "{n} KB" (reference). */
export function sizeLabel(n: number): string {
  return n < 1024 ? n + ' B' : Math.round(n / 1024) + ' KB';
}

export const utf8Size = (s: string): number => new TextEncoder().encode(s).length;

/** Reference `fmtAt`: "Oct 4, 02:30 PM" (month short, day, 2-digit hour/minute in the user's locale/calendar). */
export function fmtAt(ms: number): string {
  return new Date(ms).toLocaleString(tpLoc(), { calendar: tpCal(), month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** History row time: "Today, 14:05" / "Yesterday, 09:12" / "Oct 1, 18:40" (24-hour), relative to `now`. */
export function versionTime(ms: number, now = Date.now()): string {
  const t0 = new Date(now); t0.setHours(0, 0, 0, 0);
  const dd = Math.round((t0.getTime() - new Date(ms).setHours(0, 0, 0, 0)) / 864e5);
  const day = dd === 0 ? t('Today') : dd === 1 ? t('Yesterday') : new Date(ms).toLocaleDateString(tpLoc(), { calendar: tpCal(), month: 'short', day: 'numeric' });
  return day + (tpLoc().startsWith('fa') ? '، ' : ', ') + new Date(ms).toLocaleTimeString(tpLoc(), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

/** Version note: "Original" for version 1, "Restored version from {date}" for restores. */
export function versionNote(v: MdVersion, all: MdVersion[]): string {
  if (v.is_original) return t('Original');
  if (v.restored_from != null) {
    const src = all.find(x => x.version_no === v.restored_from);
    return t('Restored version from {date}', { date: src ? fmtAt(Date.parse(src.updated_at)) : '#' + v.restored_from });
  }
  return '';
}

export type MdMode = 'edit' | 'split' | 'preview';

export interface MdUser { name: string; ini: string; bg: string }

export interface MdTaskContext {
  projectName: string; listTitle: string | null; title: string; tags: string[];
  checklistDone: number; checklistTotal: number; deadline: string | null; fileCount: number;
}

/** "Project · List" */
export const taskWhere = (c: MdTaskContext): string => c.projectName + (c.listTitle ? ' · ' + c.listTitle : '');

/** "{done}/{n} checklist · Due {YYYY-MM-DD} · {k} file(s)" */
export function taskMeta(c: MdTaskContext): string {
  return [
    c.checklistTotal ? t('{done}/{n} checklist', { done: c.checklistDone, n: c.checklistTotal }) : '',
    c.deadline ? t('Due {date}', { date: c.deadline }) : '',
    t(c.fileCount === 1 ? '{n} file' : '{n} files', { n: c.fileCount }),
  ].filter(Boolean).join(' · ');
}
